import { inject, Injectable } from '@angular/core';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { environment } from '../../../../../environments/environment';
import { ReviewAdModel } from '../review-ad-models/review-ad.model';
import { AD_FILE_KIND_META, AdFileKind, documents } from '../../marketing-review-interfaces/marketing-review.interface';

/** File family per extension — the primary signal behind `fileKindOf()`. */
const KIND_BY_EXTENSION: Record<string, AdFileKind> = {
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', bmp: 'image', svg: 'image',
  mp4: 'video', mov: 'video', webm: 'video', m4v: 'video',
  pdf: 'pdf',
  ppt: 'slides', pptx: 'slides',
  doc: 'document', docx: 'document', txt: 'document',
  xls: 'spreadsheet', xlsx: 'spreadsheet', csv: 'spreadsheet',
};

/** Badge text per extension, so `.pptx` reads `PPT` rather than its MIME subtype. */
const BADGE_BY_EXTENSION: Record<string, string> = {
  png: 'PNG', jpg: 'JPG', jpeg: 'JPG', gif: 'GIF', webp: 'WEBP', bmp: 'BMP', svg: 'SVG',
  mp4: 'MP4', mov: 'MOV', webm: 'WEBM', m4v: 'MP4',
  pdf: 'PDF',
  ppt: 'PPT', pptx: 'PPT',
  doc: 'DOC', docx: 'DOC', txt: 'TXT',
  xls: 'XLS', xlsx: 'XLS', csv: 'CSV',
};

@Injectable({
  providedIn: 'root'
})

export class ReviewAdService {

  private sessionStorageService = inject(SessionStorageService);

  constructor() { }

  /** A single ad by id, or `undefined` when it is not in the store. */
  async getAdById(applicationId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'GET',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  async getDocumentsById(documentId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.getMrdocumentById + `/${documentId}`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'GET',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse.data);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  async getAllCommentsById(applicationId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/comments`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'GET',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  // ── Comments ────────────────────────────────────────────────────────────────
  async addComment(applicationId: number, addAdreviewComment: ReviewAdModel) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/comments`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: addAdreviewComment.toString(),
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  /** Rewrites one comment's text and stamps `editedOn`, so an edit is never silent. */
  async updateComment(applicationId: number, updateAdreviewComment: ReviewAdModel) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/comments`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'PUT',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: updateAdreviewComment.updatetoString(),
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  /** Withdraws a comment. Recorded decisions are not removable this way, by design. */
  async deleteComment(applicationId: number, commentId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/comments`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'DELETE',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: {
        commentId: commentId,
        piasRepCode: this.sessionStorageService.getCurrentRepCode()
      }
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  async complianceApprove(applicationId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/compliance-approve`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: {
        "complianceByName": this.sessionStorageService.getCurrentUserName(),
        "complianceRepCode": this.sessionStorageService.getCurrentRepCode()
      },
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  async marketingTeamApprove(applicationId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/approve`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: {
        "piasRepCode": this.sessionStorageService.getCurrentRepCode()
      },
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  async applicationReject(applicationId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/reject`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: {
        "piasRepCode": this.sessionStorageService.getCurrentRepCode()
      },
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  async applicationRequestChanges(applicationId: number) {
    const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}/return`;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: {
        "piasRepCode": this.sessionStorageService.getCurrentRepCode()
      },
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }

  async uploadAdAsset(applicationId:string,marketingMaterialTypeId:string,file: File) {
    const API_URL: string = environment.baseUrl + environment.apis.mrDocumentUpload;

    const formData = new FormData();

    formData.append('applicationId', applicationId);
    formData.append('documentTypeId', marketingMaterialTypeId);
    formData.append('file',file);

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      url: API_URL,
      headers: {
        Authorization: this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: formData
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(error.response.data, error.status);
      });
  }


  fileKindOf(attachment: string): AdFileKind {
    const byExtension: AdFileKind | undefined = KIND_BY_EXTENSION[this.extensionOf(attachment)];
    if (byExtension) {
      return byExtension;
    }

    const fileType: string = (attachment).toLowerCase();
    if (fileType.startsWith('image/')) {
      return 'image';
    }
    if (fileType.startsWith('video/')) {
      return 'video';
    }
    if (fileType === 'application/pdf') {
      return 'pdf';
    }
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) {
      return 'slides';
    }
    if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') {
      return 'spreadsheet';
    }
    if (fileType.includes('word') || fileType === 'application/msword' || fileType === 'text/plain') {
      return 'document';
    }
    return 'other';
  }

  /** Short badge for an attachment, e.g. `PNG`, `MP4`, `PPT`. */
  formatLabelOf(attachment: string): string {
    return BADGE_BY_EXTENSION[this.extensionOf(attachment)]
      ?? AD_FILE_KIND_META[this.fileKindOf(attachment)].label;
  }

  /** Material icon ligature for an attachment's kind. */
  iconOf(attachment: string): string {
    return AD_FILE_KIND_META[this.fileKindOf(attachment)].icon;
  }

  /** Palette key for an attachment's kind, used to tint its document card and badge. */
  toneOf(attachment: string): string {
    return AD_FILE_KIND_META[this.fileKindOf(attachment)].tone;
  }

  /** `1.2 MB` / `412 KB`, from the stored kilobyte size. */
  fileSizeLabel(fileSizeKb: number): string {
    return fileSizeKb >= 1024
      ? `${(fileSizeKb / 1024).toFixed(1)} MB`
      : `${fileSizeKb} KB`;
  }

  /** Lowercase extension without the dot, `''` when the name carries none. */
  private extensionOf(fileName: string): string {
    const name: string = (fileName || '').trim().toLowerCase();
    const dot: number = name.lastIndexOf('.');
    return dot > -1 ? name.slice(dot + 1) : '';
  }


}
